import type { RoleRow } from './rolesApi';
import type { ContextShape, ContextType, EffectiveAccess } from './types';

/**
 * What Settings > Roles offers, from the caller's ACTIVE effective access.
 * Mirrors pnet-identity-node role-ownership.policy.ts (roleAccessDeny,
 * tenantRoleValues, roleWriteDeny); the server re-checks every write and
 * answers per grid row whether the caller may change it (`editable`).
 *
 * - Internal staff (INTERNAL context) create roles of any type, for any
 *   organisation of that type.
 * - Tenant admins (Brand, Right holder, Agency) create and change roles of
 *   their own type, owned by their own organisation, only while the
 *   organisation's role self-service is on (roles_self_service).
 */

export const PERMISSION_ROLE_LIST = 'role_list';
export const PERMISSION_ROLE_CREATE = 'role_create';
export const PERMISSION_ROLE_EDIT = 'role_edit';
export const PERMISSION_ROLE_DELETE = 'role_delete';
export const PERMISSION_PERMISSION_CREATE = 'permission_create';
export const PERMISSION_TILES_CREATE = 'tiles_create';
export const PERMISSION_ROLE_PREVIEW = 'role_preview';

/** Who may open the page (identity ROLE_READ). */
export const ROLE_READ = [
  PERMISSION_ROLE_LIST,
  PERMISSION_ROLE_CREATE,
  PERMISSION_ROLE_EDIT,
  PERMISSION_ROLE_DELETE,
  PERMISSION_PERMISSION_CREATE,
  'user_list',
  'user_create',
  'user_edit',
];

/** The organisation that owns a tenant admin's roles (tenantOwner). */
export interface OwnOrganisation {
  type: Exclude<ContextType, 'INTERNAL'>;
  /** brands.id (Brand, Agency) or properties.id (Right holder) */
  id: number | null;
  name: string | null;
}

export interface RoleCapabilities {
  context: ContextShape | null;
  isInternal: boolean;
  /** tenant admins: their organisation's role self-service is switched off */
  selfServiceOff: boolean;
  canRead: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPermissions: boolean;
  canTiles: boolean;
  /** "Preview as this role": an INTERNAL active role with role_preview (§13.7) */
  canPreview: boolean;
  /** the role of the caller's active assignment (never offered for preview) */
  activeRoleId: number | null;
  /** tenant admins: the type and organisation every role they write gets */
  own: OwnOrganisation | null;
}

const CUSTOMER_TYPES = ['BRAND', 'RIGHT_HOLDER', 'AGENCY'];

export function roleCapabilitiesFrom(effective: EffectiveAccess | null): RoleCapabilities {
  const perms = new Set(effective?.permissions ?? []);
  const ctx = effective?.context ?? null;
  const type = String(ctx?.type ?? '');
  const isInternal = type === 'INTERNAL';
  const selfServiceOff = !isInternal && effective?.tenant_settings?.roles_self_service === false;
  const write =
    Boolean(effective) && (isInternal || CUSTOMER_TYPES.includes(type)) && !selfServiceOff;
  const own: OwnOrganisation | null =
    ctx && CUSTOMER_TYPES.includes(type)
      ? {
          type: type as OwnOrganisation['type'],
          id: (type === 'RIGHT_HOLDER' ? ctx.property_id : ctx.brand_id) ?? null,
          name: ctx.name ?? null,
        }
      : null;
  return {
    context: ctx,
    isInternal,
    selfServiceOff,
    canRead: ROLE_READ.some((p) => perms.has(p)),
    canCreate: write && perms.has(PERMISSION_ROLE_CREATE),
    canEdit: write && perms.has(PERMISSION_ROLE_EDIT),
    canDelete: write && perms.has(PERMISSION_ROLE_DELETE),
    canPermissions: write && perms.has(PERMISSION_PERMISSION_CREATE),
    canTiles: write && perms.has(PERMISSION_TILES_CREATE),
    canPreview: isInternal && perms.has(PERMISSION_ROLE_PREVIEW),
    activeRoleId:
      Number.isInteger(Number(effective?.role?.id)) && Number(effective?.role?.id) > 0
        ? Number(effective?.role?.id)
        : null,
    own,
  };
}

export interface RowActions {
  edit: boolean;
  permissions: boolean;
  tiles: boolean;
  delete: boolean;
}

/**
 * "Preview as this role" on a row: the caller may preview, the role is
 * active, it is not the role of the caller's own active assignment and not a
 * system role (roles.system_key). The server also refuses roles without one
 * organisation.
 */
export function mayPreviewRole(
  caps: RoleCapabilities | null,
  row: Pick<RoleRow, 'id' | 'roles_status'> & Partial<Pick<RoleRow, 'system_key'>>,
): boolean {
  return (
    Boolean(caps?.canPreview) &&
    row.roles_status === 1 &&
    row.id !== caps?.activeRoleId &&
    !(typeof row.system_key === 'string' && row.system_key.trim() !== '')
  );
}

/**
 * The actions of a grid row: only on rows the server marks `editable`
 * (other organisations' roles are read-only for tenant admins), each with
 * its own permission, as on the old page. A system role (system_key) is
 * never deleted: no Delete.
 */
export function rowActions(
  caps: RoleCapabilities | null,
  row: Pick<RoleRow, 'editable'> & Partial<Pick<RoleRow, 'system_key'>>,
): RowActions {
  const on = Boolean(caps) && row.editable === true;
  return {
    edit: on && Boolean(caps?.canEdit),
    permissions: on && Boolean(caps?.canPermissions),
    tiles: on && Boolean(caps?.canTiles),
    delete: on && Boolean(caps?.canDelete) && !row.system_key,
  };
}
